/**
 * Display formatting for audit values.
 *
 * The engine works in ISO (YYYY-MM-DD) because that is the only form that
 * sorts and compares correctly, and it is what the database returns. That is
 * right internally and wrong on screen: the rest of this application, and the
 * exports being audited, speak MM/DD/YYYY.
 *
 * So the conversion happens at the edge - here - rather than in the engine,
 * where changing it would break date comparison.
 *
 * Two-digit years, as asked for. Note this is display only - the full year is
 * still what the engine compared, so a shortened year can never affect a
 * verdict, only how it reads.
 */

// Matches a date anywhere in a string, so a date embedded in an explanatory
// note is converted along with a bare cell value.
//
// Two forms, because the two sides of the audit speak differently: our values
// arrive from the database as ISO, while the uploaded file writes its own
// (usually "10/1/2025", sometimes "10-01-2025"). Both are normalised, or the
// screen shows two date conventions side by side in the same row.
const ISO_DATE = /\b(\d{4})-(\d{1,2})-(\d{1,2})\b/g;
const US_DATE = /\b(\d{1,2})[/-](\d{1,2})[/-](\d{4})\b/g;

const pad = n => String(n).padStart(2, '0');
const shortYear = y => String(y).slice(2);

/**
 * "2026-08-12" and "10/1/2025" -> "08/12/26" and "10/01/25".
 *
 * Everything else is left alone. The month/day bounds check is what keeps a
 * dose off the date path: a strength like "5/0.5 mg/ml" never matches the
 * pattern, and anything that does but is not a real date (a "13/45/2025") is
 * passed through untouched rather than silently rewritten.
 */
export const displayDates = value => {
  if (value === null || value === undefined) return value;
  return String(value)
    .replace(ISO_DATE, (whole, y, m, d) =>
      Number(m) >= 1 && Number(m) <= 12 && Number(d) >= 1 && Number(d) <= 31
        ? `${pad(m)}/${pad(d)}/${shortYear(y)}`
        : whole
    )
    .replace(US_DATE, (whole, m, d, y) =>
      Number(m) >= 1 && Number(m) <= 12 && Number(d) >= 1 && Number(d) <= 31
        ? `${pad(m)}/${pad(d)}/${shortYear(y)}`
        : whole
    );
};

/** A timestamp for the results header, in the same convention. */
export const displayTimestamp = iso => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  let hours = date.getHours();
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return (
    `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${String(date.getFullYear()).slice(2)}` +
    ` ${pad(hours)}:${pad(date.getMinutes())} ${meridiem}`
  );
};

export default { displayDates, displayTimestamp };
